import React, { SelectHTMLAttributes } from "react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FormIcon } from "@core/components/forms/FormIcon";
import { cn } from "@core/lib/utils";
import { FieldErrors } from "react-hook-form";
import { ErrorMessage } from "@core/components/forms/ErrorMessage";

export type LabeledSelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string;
  icon?: IconDefinition;
  labelClassName?: string;
  selectClassName?: string;
  errors?: FieldErrors<any>;
};

/**
 * The LabeledSelect component is a customizable select element that includes a label.
 * This component allows you to style both the select and the label independently with selectClassName and labelClassName, respectively.
 * @example
 * ```tsx
 * <LabeledSelect label="PickAChoice" required labelClassName="font-bold" {...}>
 *   {myArr.map(myVar => <option key={myVar} value={myVar}>{myVar}</option>)}
 * </LabeledSelect>   
 * ```
 * They also support children, which are either displayed within the select:
 * ```tsx
 * <LabeledSelect {...}>
 *   <option key={1} value={1}>{1}</option>
 *   <option key={2} value={2}>{2}</option>
 * </LabeledSelect>
 * ```
 */
export const LabeledSelect = React.forwardRef<HTMLSelectElement, LabeledSelectProps>(
  ({ children, className, labelClassName, selectClassName, label, icon, errors, id, name, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id ?? name ?? generatedId;
    const roundedClass = icon ? "rounded-e-[var(--form-radius)] rounded-s-none" : "rounded-var[(--form-radius)]";
    const selectClasses = "dark:text-white flex h-10 w-full rounded-[var(--form-radius)] border border-neutral-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-neutral-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-800 dark:bg-neutral-950 dark:ring-offset-neutral-950 dark:placeholder:text-neutral-400 dark:focus-visible:ring-neutral-300";
    return (
      <div className={className}>
        {label && (
          <label className={cn(labelClassName, "inline-block mb-1 text-sm dark:text-white")} htmlFor={inputId}>
            {label}{props.required && <span className="text-danger-500">&nbsp;*</span>}
          </label>
        )}
        <div className="flex relative">
          {icon && <FormIcon icon={icon} />}
          <select className={cn(selectClasses, roundedClass, selectClassName)} id={inputId} name={name} {...props} ref={ref}>
            {children}
          </select>
        </div>
        {name != null && errors != null && (
          <ErrorMessage errors={errors} name={name} />
        )}
      </div>
    );
  }
);
LabeledSelect.displayName = "LabeledSelect";
